<template lang="pug">
    .account.unauthorized(v-if="!checkRestrictions('settings')")
        Error(:message="null" :code="403")
    .account.not-found(v-else-if="notFound")
        Error(:message="$t('CMS Not Found')")
    .account.not-valid(v-else-if="pathNotValid")
        Error(:message="$t('URL Not Valid')")
    .account.error(v-else-if="error")
        Error(:message="$t('Something went wrong!')" :code="501")
    .account(v-else)
        tabs(:items="tabs" :active-tab="$route.fullPath" @click="activateTab")
            template(slot="tabbar-buttons")
                el-button.delete-button(
                    circle v-if="! loading && mayDelete"
                    type="danger"
                    icon="el-icon-fa-trash-alt far"
                    :loading="deleting"
                    :disabled="isDisabledSubmit && ! deleting"
                    @click="$refs.deleteDialog.open(id)"
                )
                //- el-button.copy-button(
                //-     circle
                //-     icon="el-icon-fa-copy fas"
                //-     @click="copySettings"
                //- )
                //- Only when this form was opened FROM the websites list. Your own
                //- settings has nothing to close to, and pushing a superuser list
                //- route from there is a page a tenant cannot even open.
                el-button(
                    circle v-if="! loading && listRoute"
                    icon="el-icon-fa-times fas"
                    :disabled="isDisabledSubmit"
                    @click="leave"
                )
                el-button(
                    circle v-if="! loading && listRoute"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :disabled="isDisabledSubmit && ! savingAndClose"
                    :loading="savingAndClose"
                    @click="submit($event, listRoute)"
                )
                el-button(
                    circle v-if="! loading && checkRestrictions('settings', 'update')"
                    type="success"
                    icon="el-icon-fa-save fas"
                    :disabled="isDisabledSubmit && ! saving"
                    :loading="saving"
                    @click="submit"
                )
        el-form(label-position="top" :model="form" :rules="rules" ref="form" :validate-on-rule-change="false" :disabled="sharedShopDataReadOnly")
            all-input-fields
            el-card(shadow="always" v-loading="loading")
                router-view.account-form(ref="activeTab" :key="$route.path" :class="{'loading-panel': loading}" @request-save="submit")
            .buttons-flex
                .form-buttons
                    //- el-button(
                    //-     round
                    //-     icon="el-icon-fa-copy fas"
                    //-     @click="copySettings"
                    //- ) {{ $t('Copy Settings') }}
                .form-buttons
                    el-button(
                        round v-if="! loading && listRoute"
                        icon="el-icon-fa-times fas"
                        :disabled="isDisabledSubmit"
                        @click="leave"
                    ) {{ $t('Dismiss') }}
                    el-button(
                        round v-if="! loading && listRoute"
                        type="primary"
                        icon="el-icon-fa-check fas"
                        :loading="savingAndClose"
                        :disabled="isDisabledSubmit && ! savingAndClose"
                        @click="submit($event, listRoute)"
                    ) {{ $t('Save and close') }}
                    el-button(
                        round v-if="! loading && checkRestrictions('settings', 'update')"
                        type="success"
                        icon="el-icon-fa-save fas"
                        :loading="saving"
                        :disabled="isDisabledSubmit && ! saving"
                        @click="submit"
                    ) {{ $t('Save') }}
        confirm-delete-dialog(
            ref="deleteDialog"
            :title="$t('Delete website')"
            :message="$t('The website is deleted together with its domains and team access.')"
            @confirm="deleteCurrentCms"
        )
</template>

<i18n>
{
    "de": {
        "Knowledge Base": "Wissensdatenbank",
        "CMS dependencies could not be fetched.": "CMS-Abhängigkeiten konnten nicht abgerufen werden.",
        "CMS could not be fetched.": "CMS konnte nicht gefunden werden.",
        "CMS could not be saved.": "CMS konnte nicht gespeichert werden.",
        "CMS could not be deleted.": "CMS konnte nicht gelöscht werden.",
        "CMS deleted successfully!": "CMS erfolgreich gelöscht!",
        "Delete website": "Webauftritt löschen",
        "The website is deleted together with its domains and team access.": "Der Webauftritt wird mit seinen Domains und Teamzugängen gelöscht.",
        "CMS saved successfully!": "CMS erfolgreich gespeichert!",
        "CMS Not Found": "CMS nicht gefunden",
        "Account": "Account",
        "Addresses": "Adressen",
        "Websites": "Webauftritte",
        "Team": "Team",
        "Basic": "Basis",
        "APIs and external code": "APIs und Fremdcode",
        "Shop": "Shop",
        "Stocks": "Lager",
        "Points of sale": "Kassen",
        "Shipping methods": "Versandmethoden",
        "Payment methods": "Zahlungsarten",
        "Currencies": "Währungen",
        "Sections": "Inhaltselemente",
        "Document Templates": "Dokumentvorlagen",
        "Billing": "Abrechnung",
        "Business Card": "Visitenkarte",
        "Global Contents": "Globale Inhalte",
        "Communication": "Kommunikation",
        "Artificial Intelligence": "Künstliche Intelligenz"
    }
}
</i18n>

<script>
import _ from 'lodash';
import uniqid from 'uniqid'

import { checkRestrictions } from '@/helpers'

import router from '@/router';
import { useAuthStore, useSettingsStore, useSetupStore } from '@/stores'

import Tabs from '@/components/Tabs';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import Error from '@/components/Error';

// validate all fields, regardless if the corresponding component is mounted or not
import AllInputFields from './AllInputFields';

export default {
    name: 'Settings',
    data() {
        return {
            error: false,
            notFound: false,
            pathNotValid: false,
            loading: false,
            saving: false,
            savingAndClose: false,
            deleting: false,
            prePath: '/settings'
        };
    },
    components: {
        Tabs,
        ConfirmDeleteDialog,
        Error,
        AllInputFields
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) {
                this.setInitializedFlag(false);
                this.initialize();
            }
        },
        // Per the project's documented el-form-item + v-if listener bug: when
        // chain-of-stores flips ON, BusinessCardsPerStock and its nested
        // el-form-items mount inside a previously-false v-if block. Their
        // mounted() registered listeners may not see the form-level rules
        // yet — re-register on $nextTick.
        'form.has_business_card_per_stock': function (newValue) {
            if (!newValue) return;
            this.$nextTick(() => {
                const formRef = this.$refs.form;
                if (!formRef || !formRef.fields) return;
                formRef.fields.forEach((field) => {
                    if (field.prop && field.prop.indexOf('businessCards.') === 0) {
                        field.removeValidateEvents();
                        field.addValidateEvents();
                    }
                });
            });
        },
        // template() {
        //     this.syncSettingFields();
        // }
    },
    computed: {
        template: {
            get() {
                return _.get(this.form, 'config.template')
            },
            set(value) {
                // store.commit(`settings/form/template`, value);
                // this.syncSettingFields();
            }
        },
        requirements() {
            return _.get(this.template, 'requirements') || []
        },
        needsMapBox() {
            return !!this.requirements.find(requirement => {
                return requirement.key === 'mapbox' && requirement.context === 'cms'
            })
        },
        needsGoogleRecaptcha() {
            return !!this.requirements.find(requirement => {
                return requirement.key === 'google-recaptcha-v3' && requirement.context === 'cms'
            })
        },
        needsSmtpCredentials() {
            return !!this.requirements.find(requirement => {
                return requirement.key === 'smtp-credentials' && requirement.context === 'cms'
            })
        },
        needsPayPal() {
            return this.shop && this.requirements.find(requirement => {
                return requirement.key === 'paypal-checkout' && requirement.context === 'shop'
            })
        },
        needsStripe() {
            return this.shop && this.requirements.find(requirement => {
                return requirement.key === 'stripe-checkout' && requirement.context === 'shop'
            })
        },
        id() {
            return _.get(this.form, 'id', false);
        },
        /**
         * Where "close" goes, and null when there is nowhere to go.
         *
         * A superuser editing ANOTHER website arrives at /settings/:id/... from
         * the websites list, so closing returns there. Your own settings has no
         * `:id` and no list behind it — this used to push /system/abonements
         * regardless, which for a tenant is a page they cannot even open.
         */
        listRoute() {
            return this.$route.params.id ? { name: 'system-abonements' } : null;
        },
        isDisabledSubmit() {
            return this.saving || this.savingAndClose || this.deleting;
        },
        /**
         * Only a superuser holding "Kundenkonten und Webauftritte" deletes a
         * website, and only from the websites list: a tenant's own settings
         * never offer it. The API refuses everybody else on its own.
         */
        mayDelete() {
            return !! this.listRoute && useAuthStore().platformRightKeys.indexOf('platform:accounts') !== -1;
        },
        tabs() {
            let tabs = [
                {
                    name: this.$t('Basic'),
                    path: this.prePath + '/basic',
                    icon: {
                        name: 'gear',
                        group: 'fas'
                    }
                }
            ]

            // business card tab (shown when business card billing feature is enabled)
            if (this.form.is_business_card) {
                tabs.push({
                    name: this.$t('Business Card'),
                    path: this.prePath + '/business-card',
                    icon: {
                        name: 'address-card',
                        group: 'fas'
                    }
                })
            }

            // billing tab
            tabs.push({
                name: this.$t('Billing'),
                path: this.prePath + '/billing',
                icon: {
                    name: 'file-invoice-dollar',
                    group: 'fas'
                }
            })

            // SMTP / newsletter config moved to the Communication tab.
            if (this.needsMapBox
                || this.needsGoogleRecaptcha
                || this.needsPayPal
                || this.needsStripe
            ) {
                tabs.push({
                    name: this.$t('APIs and external code'),
                    path: this.prePath + '/api',
                    icon: {
                        name: 'key',
                        group: 'fas'
                    }
                })
            }

            // settings tab
            // const chosenTemplate = this.options.template.items.find(template => template.id == this.template.id);
            // const settings = _.get(this.form, 'config.template.settings')
            //     || _.get(this.activeCms, 'config.template.settings')
            //     || []

            const settings = _.get(this.form, 'config.template.settings') || []

            if (settings.find(el => el.key === 'design')) {
                tabs.push({
                    name: this.$t('Design'),
                    path: this.prePath + '/design',
                    icon: {
                        name: 'brush',
                        group: 'fas'
                    }
                })
            }

            if (settings.find(el => el.key === 'sections')) {
                tabs.push({
                    name: this.$t('Sections'),
                    path: this.prePath + '/sections',
                    icon: {
                        name: 'puzzle-piece',
                        group: 'fas'
                    }
                })
            }

            let globalContents = _.get(this.form, 'config.template.globalContents')
            if (globalContents) {
                tabs.push({
                    name: this.$t('Global Contents'),
                    path: this.prePath + '/global-contents',
                    icon: {
                        name: 'globe',
                        group: 'fas'
                    }
                })
            }

            // document templates tab
            tabs.push({
                name: this.$t('Document Templates'),
                path: this.prePath + '/document-templates',
                icon: {
                    name: 'file',
                    group: 'fas'
                }
            })

            // Communication tab — hosts the transactional + newsletter SMTP config
            // (needed by shop/forms/newsletter CMSes) plus, with the communication
            // feature on, the WhatsApp/messenger channels and signatures.
            const hasCommunicationFeature = this.form.extensions.find(extension => extension.key === 'communication') && checkRestrictions(['ex:comm', 'ex:comm:accounts'])
            const hasNewsletter = this.form.extensions.find(extension => extension.key === 'newsletter')
            if (hasCommunicationFeature || hasNewsletter || this.needsSmtpCredentials) {
                tabs.push({
                    name: this.$t('Communication'),
                    path: this.prePath + '/communication',
                    icon: {
                        name: 'comments',
                        group: 'far'
                    }
                })
            }

            // shop tab
            if (this.form.extensions.find(extension => extension.key === 'shop') && checkRestrictions('ex:shop')) {
                tabs.push({
                    name: this.$t('Shop'),
                    path: this.prePath + '/shop',
                    icon: {
                        name: 'shopping-cart',
                        group: 'fas'
                    }
                })
            }

            // stocks tab
            if (this.form.extensions.find(extension => extension.key === 'shop') && checkRestrictions(['ex:shop', 'ex:shop:stocks'])) {
                tabs.push({
                    name: this.$t('Stocks'),
                    path: this.prePath + '/stocks',
                    icon: {
                        name: 'house',
                        group: 'fas'
                    }
                })
            }

            // pos tab
            // Requires the point-of-sale extension, not just shop - the main menu
            // has always gated its POS entries that way, and this tab did not, so a
            // shop without POS still saw an empty Kassen tab.
            if (this.form.extensions.find(extension => extension.key === 'shop')
                && this.form.extensions.find(extension => extension.key === 'shop:point-of-sale')
                && checkRestrictions(['ex:shop', 'ex:pos', 'ex:pos:points-of-sale'])) {
                tabs.push({
                    name: this.$t('Points of sale'),
                    path: this.prePath + '/pos',
                    icon: {
                        name: 'cash-register',
                        group: 'fas'
                    }
                })
            }

            // shipping-methods tab
            if (this.form.extensions.find(extension => extension.key === 'shop') && checkRestrictions('ex:shop')) {
                tabs.push({
                    name: this.$t('Shipping methods'),
                    path: this.prePath + '/shipping-methods',
                    icon: {
                        name: 'truck',
                        group: 'fas'
                    }
                })
            }

            // shipping-methods tab
            if (this.form.extensions.find(extension => extension.key === 'shop') && checkRestrictions('ex:shop')) {
                tabs.push({
                    name: this.$t('Payment methods'),
                    path: this.prePath + '/payment-methods',
                    icon: {
                        name: 'credit-card',
                        group: 'fas'
                    }
                })
            }

            // (Return reasons live inside the Shop tab — no dedicated tab.)

            // currencies tab
            if (this.form.extensions.find(extension => extension.key === 'shop') && checkRestrictions('ex:shop')) {
                tabs.push({
                    name: this.$t('Currencies'),
                    path: this.prePath + '/currencies',
                    icon: {
                        name: 'coins',
                        group: 'fas'
                    }
                })
            }

            // pos tab
            // if (this.form.extensions.find(extension => extension.key === 'shop')) {
            //     tabs.push({
            //         name: "POS",
            //         path: this.prePath + '/shop',
            //         icon: {
            //             name: 'shopping-cart',
            //             group: 'fas'
            //         }
            //     })
            // }

            // knowledge base tab: shared by the chatbot and KI-Inhalte
            const hasChatbot = !! this.form.extensions.find(extension => extension.key === 'chatbot')
            if (hasChatbot || this.form.extensions.find(extension => extension.key === 'ai-content')) {
                tabs.push({
                    name: this.$t('Knowledge Base'),
                    path: this.prePath + '/knowledge-base',
                    icon: {
                        name: 'book',
                        group: 'fas'
                    }
                })
            }

            // chatbot tab: its persona
            if (hasChatbot) {
                tabs.push({
                    name: this.$t('Chatbot'),
                    path: this.prePath + '/chatbot',
                    icon: {
                        name: 'robot',
                        group: 'fas'
                    }
                })
            }

            // AI tab — always present. The Art. 50(4) disclosure setting inside it
            // applies to every shop, including one with no generative-media features:
            // an image made with AI elsewhere and uploaded here carries the same duty.
            // The model defaults within the tab stay feature-gated.
            tabs.push({
                name: this.$t('Artificial Intelligence'),
                path: this.prePath + '/ai',
                icon: {
                    name: 'wand-magic-sparkles',
                    group: 'fas'
                }
            })

            return tabs;
        },
        user() {
            return useAuthStore().user;
        },
        isSuperUser() {
            return useAuthStore().isSuperUser;
        },
        form() {
            return useSettingsStore().form;
        },
        initialized() {
            return useSettingsStore().formInitialized;
        },
        validateOnEnter() {
            return useSettingsStore().formValidateOnEnter;
        },
        rules() {
            return useSettingsStore().rules;
        },
        activeCmsIndex() {
            return useSettingsStore().activeCmsIndex;
        },
        options() {
            return useSettingsStore().options;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        setupStore() {
            return useSetupStore();
        }
    },
    methods: {
        // syncSettingFields() {
        //     const chosenTemplate = this.options.template.items.find(template => template.id == this.template.id);

        //     if (chosenTemplate !== undefined) {
        //         if (chosenTemplate.settings != null && chosenTemplate.settings.length > 0) {
        //             for (const setting of chosenTemplate.settings) {
        //                 if (!setting.blocks || !setting.blocks.length) return;
        //                 for (const block of setting.blocks) {
        //                     if (!block.groups || !block.groups.length) return;
        //                     for (const group of block.groups) {
        //                         if (!group.fields || !group.fields.length) return;
        //                         for (const field of group.fields) {
        //                             const isPresent = this.form.settings.some(item => item.key == field.key && item.template_key == chosenTemplate.id);
        //                             const settingField = {
        //                                 id: uniqid('new:'),
        //                                 template_key: chosenTemplate.id,
        //                                 key: field.key,
        //                                 value: field.default,
        //                             }
        //                             if (!isPresent && field.key !== undefined && field.type != "upload") {
        //                                 store.commit('settings/form/addSetting', settingField);
        //                             }
        //                         }
        //                     }
        //                 }
        //             }
        //         }
        //     }
        // },
        initialize() {
            let pathId = this.$route.params.id

            if (this.initialized !== true) {
                this.setInitializedFlag(true);

                if (pathId !== undefined) {
                    this.prePath = '/settings/' + pathId
                }

                let actions = [
                    this.fetchTemplateOptions(),
                    this.fetchDocumentTemplatePartialPresetOptions(),
                ];

                const fetchDependencies = () => {
                    if (this.id) {
                        actions.push(this.fetchCmsOptions(this.id))
                        actions.push(this.fetchMemberTagOptions(this.id))
                    }

                    return Promise.all(actions);
                };

                this.error = false;
                this.loading = true;

                if (pathId === 'new') {
                    fetchDependencies()
                        .then(() => {
                            this.loading = false;
                            useSettingsStore().reset();
                            // this.syncSettingFields();

                        })
                        .catch(err => {
                            this.loading = false;
                            this.error = true;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('CMS dependencies could not be fetched.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                } else if (!isNaN(pathId) || pathId === undefined) {

                    let accountID;
                    if (pathId === undefined) {
                        // Wait for activeCms to be available - watcher will call initialize() again
                        if (!this.activeCms || !this.activeCms.id) {
                            this.setInitializedFlag(false);
                            return;
                        }
                        accountID = this.activeCms.id
                    }
                    else if (!isNaN(pathId)) {
                        accountID = pathId
                    }

                    this.fetchCms(accountID)
                        .then(() => {

                            this.loading = false;
                            this.notFound = false;

                            // fetch options for dropdowns
                            fetchDependencies()
                                .then(() => {
                                    // this.syncSettingFields();
                                    // rules are now derived via getter from form state — no manual reset needed.
                                })
                                .catch(err => {
                                    this.error = true;

                                    /* eslint no-console: ["error", { allow: ["error"] }] */
                                    console.error(err);

                                    this.$notify.error({
                                        title: this.$t('An error occurred!'),
                                        message: this.$t('CMS dependencies could not be fetched.'),
                                        iconClass: 'fas el-icon-fa-xmark',
                                        position: 'bottom-left',
                                        customClass: 'dark',
                                        duration: 8000
                                    });
                                });
                        })
                        .catch(err => {
                            this.loading = true;
                            this.notFound = true;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('CMS could not be fetched.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                } else {
                    this.loading = true;
                    this.pathNotValid = true;
                }
            } else {
                if (this.validateOnEnter === true) {
                    setTimeout(() => {
                        this.$refs.form.validate(() => {
                            // do nothing
                            // callback function is required :(
                        });
                    }, 100);
                }
            }
        },
        async submit(event, redirect = false) {
            this.$refs.form.validate(async (valid, errors) => {
                if (valid) {
                    if (redirect) this.savingAndClose = true;
                    else this.saving = true;

                    // Check if active tab has pending card data (billing tab)
                    const activeTab = _.get(this.$refs, 'activeTab');
                    if (activeTab && typeof activeTab.confirmPendingCard === 'function') {
                        try {
                            await activeTab.confirmPendingCard();
                        } catch (error) {
                            // Card confirmation failed, stop saving
                            if (redirect) this.savingAndClose = false;
                            else this.saving = false;
                            return;
                        }
                    }

                    // Let the active tab flush its own deferred settings (e.g. the
                    // communication tab's auto-replies) as part of this same Save.
                    if (activeTab && typeof activeTab.saveTabSettings === 'function') {
                        try {
                            await activeTab.saveTabSettings();
                        } catch (error) {
                            if (redirect) this.savingAndClose = false;
                            else this.saving = false;
                            return;
                        }
                    }


                    let save = this.create;
                    if (this.id) save = this.update;

                    let form = _.cloneDeep(this.form);
                    const administrators = _.get(form, 'administrators', []);
                    if (administrators.length) {
                        form.administrators.forEach((administrator, i) => {
                            const template = _.get(administrator, 'cms.config.template.id');
                            form.administrators[i].cms.config.template = template;
                        });
                    }
                    save(form)
                        .then(cms => {
                            this.$nextTick(() => {
                                this.setupStore
                                    .fetchSetup()
                                    .then(() => {
                                        this.$notify.success({
                                            title: this.$t('CMS saved successfully!'),
                                            iconClass: 'fas el-icon-fa-check',
                                            position: 'bottom-left',
                                            customClass: 'dark',
                                            duration: 8000
                                        });

                                        // redirect if new cms created
                                        if (this.prePath === "/settings/new") {
                                            this.setInitializedFlag(false);
                                            router.push({ name: 'settings-form', params: { id: this.form.id } });
                                        }

                                        if (redirect) {
                                            router.push(redirect);
                                        } else {
                                            if (this.$route.params.id === 'new') {
                                                const route = router.currentRoute
                                                route.params.id = cms.id
                                                router.replace(route)
                                            }
                                        }
                                    })
                                    .finally(() => {
                                        if (redirect) this.savingAndClose = false;
                                        else this.saving = false;
                                    });
                            });
                        })
                        .catch(err => {
                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error('Save CMS error:', err);
                            if (redirect) this.savingAndClose = false;
                            else this.saving = false;

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('CMS could not be saved.'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                } else {
                    this.setValidateOnEnterFlag(true);

                    // check the invalid fields
                    // and go to the corresponding tab
                    // or open the corresponding closed area
                    // TODO: scroll to invalid field

                    let invalidTabs = [];
                    let invalidBusinessCardIndices = [];
                    _.each(errors, (error, path) => {
                        //TODO Validierung für CMS anpassen
                        if (_.indexOf(['gender', 'first_name', 'last_name', 'email', 'password'], path) >= 0) {
                            invalidTabs.push(this.prePath + '/basic');
                        }
                        // Business card tab — single-card and chain-of-stores
                        // both validate under the /business-card tab path.
                        if (_.startsWith(path, 'businessCard.') || _.startsWith(path, 'businessCards.')) {
                            invalidTabs.push(this.prePath + '/business-card');
                        }
                        // Capture which per-stock accordion item is invalid so
                        // we can pop it open after navigating to the tab.
                        if (_.startsWith(path, 'businessCards.')) {
                            const parts = _.split(path, '.');
                            const idx = parseInt(parts[1], 10);
                            if (!isNaN(idx)) {
                                invalidBusinessCardIndices.push(idx);
                            }
                        }
                        // if (_.startsWith(path, 'addresses.')) {
                        //     invalidTabs.push(`/settings/${this.$route.params.id}/addresses`);
                        // }
                        // if (_.startsWith(path, 'administrators.')) {
                        //     invalidTabs.push(`/settings/${this.$route.params.id}/abonements`);
                        // }
                    });

                    // Tell the per-stock accordion which panels to open via a
                    // store-side hint — BusinessCardsPerStock watches this and
                    // pops the matching el-collapse-item open on next render.
                    if (invalidBusinessCardIndices.length) {
                        const stockIds = _.uniq(invalidBusinessCardIndices)
                            .map((index) => {
                                const card = _.get(useSettingsStore().form, `businessCards.${index}`);
                                return card && card.stock ? parseInt(card.stock.id, 10) : null;
                            })
                            .filter((id) => id !== null);
                        useSettingsStore().setInvalidBusinessCardStockIds(stockIds);
                    }

                    invalidTabs = _.uniq(invalidTabs);

                    // check if the current tab
                    // is present in invalidTabs
                    // if not, go to next (first) invalid tab

                    const match = _.indexOf(invalidTabs, this.$route.fullPath);

                    let switchTab = false;
                    if (match < 0) {
                        invalidTabs.every(invalidTab => {
                            if (invalidTab && this.activeTab !== invalidTab) {
                                const tab = _.find(this.tabs, { path: invalidTab });
                                if (tab) {
                                    this.activateTab(tab);
                                    switchTab = true;

                                    // break loop after the first tab has been activated
                                    return false;
                                }
                            }
                        });
                    }

                    // After a tab switch (router-view tears down/mounts), the
                    // newly-mounted *visible* form-items haven't been validated
                    // yet (validate() ran against the always-mounted shadows
                    // in AllInputFields). Re-run validate() once the route
                    // transition has settled so red error states render on
                    // the actual fields the user sees. setTimeout > $nextTick
                    // because the route resolution + component mount happens
                    // across multiple ticks.
                    if (switchTab) {
                        setTimeout(() => {
                            if (this.$refs.form) {
                                this.$refs.form.validate(() => {});
                            }
                        }, 200);
                    }

                    // // triger the validation again
                    // // so the previously hidden fields will get the errors
                    // // ---
                    // // does not work if the component has been destroyed
                    // // in the meantime (f.a. by switching tabs)
                    // if (switchTab === false) {
                    //     setTimeout(() => {
                    //         this.$refs.form.validate(() => {
                    //             // do nothing
                    //             // callback function is required :(
                    //         })
                    //     }, 100);
                    // }

                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }
            });
        },
        leave() {
            router.push(this.listRoute);
        },
        deleteCurrentCms(id) {
            this.deleting = true;
            this.delete(id)
                .then(() => {
                    router.push(this.listRoute);

                    this.$notify.success({
                        title: this.$t('CMS deleted successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);

                    this.deleting = false;

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('CMS could not be deleted.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                });
        },
        checkRestrictions,
        setInitializedFlag(flag) {
            useSettingsStore().setFormInitialized(flag);
        },
        setValidateOnEnterFlag(flag) {
            useSettingsStore().setFormValidateOnEnter(flag);
        },
        fetchCms(id) {
            return useSettingsStore().fetchCms(id);
        },
        fetchTemplateOptions() {
            return useSettingsStore().fetchTemplateOptions();
        },
        fetchDocumentTemplatePartialPresetOptions() {
            return useSettingsStore().fetchDocumentTemplatePartialPresetOptions();
        },
        fetchCmsOptions(ownerID) {
            return useSettingsStore().fetchCmsOptions(ownerID);
        },
        fetchMemberTagOptions(cmsID) {
            return useSettingsStore().fetchMemberTagOptions(cmsID);
        },
        create(cms) {
            return useSettingsStore().create(cms);
        },
        update(cms) {
            return useSettingsStore().update(cms);
        },
        delete(id) {
            return useSettingsStore().delete(id);
        },
        activateTab(item) {
            return this.setupStore.activateTab(item);
        }
    },
    mounted() {
        this.initialize();
    },
    beforeRouteEnter(to, from, next) {
        const id = _.get(to, 'params.id');

        if (to.name === 'settings-form') {
            router.push({ name: 'settings-basic', params: { id } })
        }
        else {
            next();
        }
    },
    beforeRouteUpdate(to, from, next) {
        if (to.path === "/settings" && from.path.includes("/settings/new")) {
            this.setInitializedFlag(false);
        }

        if (this.initialized !== true) {
            const id = _.get(to, 'params.id');
            if (to.name === 'settings-form') {
                next({ name: 'settings-basic', params: { id } });
            } else {
                next();
            }
            this.$nextTick(() => {
                this.initialize(id)
            })
        } else {
            const number = Number(from.params.id);
            if (Number.isInteger(number) && to.path == "/settings") {
                const id = undefined
                this.setInitializedFlag(false);
                router.push({ name: 'settings-basic', params: { id } })
            }
            if (!(from.params.id === undefined && to.path == "/settings")) {
                next();
            }
        }
    },
    beforeRouteLeave(to, from, next) {

        next();

        // TODO: add reset action
        // see event form

        // issue: after saving an account, the life cycle methods
        // destroyed, beforeRouteLeave, etc. are gettings triggered

        // workaround
        // check if there is the route /system/account/:id
        // in the to.matched collection

        const leave = !to.matched.find(route => route.path === '/settings/:id');
        if (leave) {
            const settingsStore = useSettingsStore();
            settingsStore.reset();
            settingsStore.resetUI();
            // resetRules() was removed from the store — `rules` is now a getter derived
            // from `form`, so reset() above already resets the rules.
        }
    }
};
</script>

<style lang="sass" scoped>
.account
    max-width: 100%
    :deep()
        .el-card__body
            padding: 0
            & > section
                padding: 20px
.el-main.main-slot > .Main__view
    // max-width: 2304px
// .flex-buttons
//     display: flex
//     justify-content: space-between
</style>
